import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, PenLine } from "lucide-react";
import { useLang, usePageMeta } from "@/App";
import { Reveal } from "@/components/Section";

const TARGET = new Date("2030-01-01T00:00:00").getTime();
const IMG_ROCKET = "https://images.unsplash.com/photo-1517976487492-5750f3195933?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";
const IMG_STARBOX = "https://static.prod-images.emergentagent.com/jobs/1fa201f8-a715-43e1-a703-f98cb3ad5e9a/images/8482e3d17c21e3fe9b1fe39bc8a7f5fe96e0c8f1100ee5f1ad5160425c9eb21f.jpeg";
const IMG_DESAL = "https://static.prod-images.emergentagent.com/jobs/1fa201f8-a715-43e1-a703-f98cb3ad5e9a/images/855131859af2cc2fe868b80a540c3bd32d31d929d241467ff9684190bf88e6ec.jpeg";
const IMG_SAT = "https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const STR = {
  fr: {
    kicker: "INGENIA — le site industriel de l'inventeur",
    subtitle: "Le premier avion-fusée à dépasser Mach 10",
    countdownLabel: "Premier vol hypersonique",
    units: ["Jours", "Heures", "Min", "Sec"],
    intro: [
      "Mach 10 — environ 12 300 kilomètres-heure, cinq fois la vitesse du Concorde. MELOMANIA est le programme d'avion-fusée porté par Gilles de Buretel de Chassey : franchir ce cap en 2030, avec un démonstrateur avant la version habitée.",
      "Comme toujours chez cet industriel enraciné dans la vie, la performance n'est jamais une fin : c'est une promesse faite à l'humain — voyager autrement, plus vite, plus proprement.",
    ],
    specsTitle: "Fiche programme",
    specs: [
      { v: "Mach 10", l: "Vitesse visée" },
      { v: "≈ 12 300 km/h", l: "Soit 5× le Concorde" },
      { v: "2030", l: "Premier vol" },
      { v: "Démonstrateur", l: "Avant la version habitée" },
    ],
    logbook: {
      title: "Journal de bord — la route vers 2030",
      steps: [
        { year: "2026", title: "Études & conception", text: "Études de faisabilité, architecture de l'avion-fusée, conception du démonstrateur.", current: true },
        { year: "2027", title: "Propulsion", text: "Essais au sol du moteur-fusée et de la chaîne propulsive.", current: false },
        { year: "2028", title: "Démonstrateur", text: "Premier vol du démonstrateur inhabité, montée en puissance.", current: false },
        { year: "2029", title: "Vols d'essais", text: "Campagne de vols supersoniques puis hypersoniques progressifs.", current: false },
        { year: "2030", title: "Mach 10", text: "Le cap : premier vol à Mach 10, cinq fois le Concorde.", current: false },
      ],
    },
    familyTitle: "La famille des aéronefs",
    family: [
      { name: "MELOMANIA", desc: "Premier avion-fusée à dépasser Mach 10 — horizon 2030.", featured: true },
      { name: "LE SWAN", desc: "Le vol dans sa plus pure élégance.", featured: false },
      { name: "LA MELUSINE", desc: "La fée des airs, entre rêve et ingénierie.", featured: false },
    ],
    modulesTitle: "Les modules INGENIA — l'invention au service du quotidien",
    modules: [
      { name: "ARCHANGE", img: IMG_SAT, desc: "Connectivité par satellites en orbite basse, incluse de base dans l'offre.", specs: "Orbite basse · supervision 24/7" },
      { name: "STARBOX", img: IMG_STARBOX, desc: "Hydrogène vert produit à la demande sur site, à partir de ferraille recyclée.", specs: "120 kg H₂/jour · 3 t de ferraille valorisées/jour · 9 t de CO₂ évitées/mois" },
      { name: "DESSALEMENT", img: IMG_DESAL, desc: "L'eau de mer ou saumâtre devient potable sur site, alimentée par l'énergie verte.", specs: "20 m³/jour · 100 % renouvelable · 24/7" },
    ],
    prosumerTitle: "Le PROSUMER — la troisième révolution industrielle",
    prosumerText: "Chaque site INGENIA produit et consomme localement son énergie, son eau et son hydrogène, puis partage ses surplus avec ses voisins via un réseau distribué et intelligent.",
    offer: "L'offre INGENIA : 100 €/mois, service et hotline 24/24 inclus — installé sous huitaine.",
    ctaSite: "Visiter ingenia-services.com",
    ctaContact: "Écrire à l'inventeur",
  },
  en: {
    kicker: "INGENIA — the inventor's industrial site",
    subtitle: "The first rocket plane to break Mach 10",
    countdownLabel: "First hypersonic flight",
    units: ["Days", "Hours", "Min", "Sec"],
    intro: [
      "Mach 10 — roughly 12,300 kilometres per hour, five times the speed of Concorde. MELOMANIA is the rocket-plane programme championed by Gilles de Buretel de Chassey: crossing that threshold in 2030, with a demonstrator before the crewed version.",
      "As always with this industrialist rooted in life, performance is never an end in itself: it is a promise made to people — travelling differently, faster, more cleanly.",
    ],
    specsTitle: "Programme sheet",
    specs: [
      { v: "Mach 10", l: "Target speed" },
      { v: "≈ 12,300 km/h", l: "5× Concorde" },
      { v: "2030", l: "First flight" },
      { v: "Demonstrator", l: "Before the crewed version" },
    ],
    logbook: {
      title: "Logbook — the road to 2030",
      steps: [
        { year: "2026", title: "Studies & design", text: "Feasibility studies, rocket-plane architecture, demonstrator design.", current: true },
        { year: "2027", title: "Propulsion", text: "Ground testing of the rocket engine and propulsion chain.", current: false },
        { year: "2028", title: "Demonstrator", text: "First flight of the uncrewed demonstrator, ramping up.", current: false },
        { year: "2029", title: "Test flights", text: "Progressive supersonic then hypersonic flight campaign.", current: false },
        { year: "2030", title: "Mach 10", text: "The milestone: first flight at Mach 10, five times Concorde.", current: false },
      ],
    },
    familyTitle: "The aircraft family",
    family: [
      { name: "MELOMANIA", desc: "The first rocket plane to break Mach 10 — by 2030.", featured: true },
      { name: "THE SWAN", desc: "Flight in its purest elegance.", featured: false },
      { name: "THE MELUSINE", desc: "The fairy of the skies, between dream and engineering.", featured: false },
    ],
    modulesTitle: "The INGENIA modules — invention in the service of everyday life",
    modules: [
      { name: "ARCHANGE", img: IMG_SAT, desc: "Low-orbit satellite connectivity, included in the offer.", specs: "Low orbit · 24/7 monitoring" },
      { name: "STARBOX", img: IMG_STARBOX, desc: "Green hydrogen produced on demand on site, from recycled scrap metal.", specs: "120 kg H₂/day · 3 t of scrap recovered/day · 9 t of CO₂ avoided/month" },
      { name: "DESSALEMENT", img: IMG_DESAL, desc: "Sea or brackish water becomes drinkable on site, powered by green energy.", specs: "20 m³/day · 100% renewable · 24/7" },
    ],
    prosumerTitle: "The PROSUMER — the third industrial revolution",
    prosumerText: "Every INGENIA site produces and consumes its own energy, water and hydrogen locally, then shares surpluses with neighbours through a smart, distributed network.",
    offer: "The INGENIA offer: €100/month, service and 24/7 hotline included — installed within a week.",
    ctaSite: "Visit ingenia-services.com",
    ctaContact: "Write to the inventor",
  },
  es: {
    kicker: "INGENIA — el sitio industrial del inventor",
    subtitle: "El primer avión-cohete en superar Mach 10",
    countdownLabel: "Primer vuelo hipersónico",
    units: ["Días", "Horas", "Min", "Seg"],
    intro: [
      "Mach 10 — unos 12.300 kilómetros por hora, cinco veces la velocidad del Concorde. MELOMANIA es el programa de avión-cohete impulsado por Gilles de Buretel de Chassey: franquear ese umbral en 2030, con un demostrador antes de la versión tripulada.",
      "Como siempre en este industrial arraigado en la vida, el rendimiento nunca es un fin: es una promesa hecha al ser humano — viajar de otro modo, más rápido, más limpio.",
    ],
    specsTitle: "Ficha del programa",
    specs: [
      { v: "Mach 10", l: "Velocidad objetivo" },
      { v: "≈ 12.300 km/h", l: "5× el Concorde" },
      { v: "2030", l: "Primer vuelo" },
      { v: "Demostrador", l: "Antes de la versión tripulada" },
    ],
    logbook: {
      title: "Diario de a bordo — rumbo a 2030",
      steps: [
        { year: "2026", title: "Estudios y diseño", text: "Estudios de viabilidad, arquitectura del avión-cohete, diseño del demostrador.", current: true },
        { year: "2027", title: "Propulsión", text: "Ensayos en tierra del motor cohete y de la cadena propulsiva.", current: false },
        { year: "2028", title: "Demostrador", text: "Primer vuelo del demostrador no tripulado, aumento de potencia.", current: false },
        { year: "2029", title: "Vuelos de ensayo", text: "Campaña de vuelos supersónicos y luego hipersónicos progresivos.", current: false },
        { year: "2030", title: "Mach 10", text: "El hito: primer vuelo a Mach 10, cinco veces el Concorde.", current: false },
      ],
    },
    familyTitle: "La familia de aeronaves",
    family: [
      { name: "MELOMANIA", desc: "El primer avión-cohete en superar Mach 10 — 2030.", featured: true },
      { name: "EL SWAN", desc: "El vuelo en su más pura elegancia.", featured: false },
      { name: "LA MELUSINE", desc: "El hada de los cielos, entre sueño e ingeniería.", featured: false },
    ],
    modulesTitle: "Los módulos INGENIA — la invención al servicio de lo cotidiano",
    modules: [
      { name: "ARCHANGE", img: IMG_SAT, desc: "Conectividad por satélites de órbita baja, incluida en la oferta.", specs: "Órbita baja · supervisión 24/7" },
      { name: "STARBOX", img: IMG_STARBOX, desc: "Hidrógeno verde producido a demanda in situ, a partir de chatarra reciclada.", specs: "120 kg H₂/día · 3 t de chatarra valorizadas/día · 9 t de CO₂ evitadas/mes" },
      { name: "DESSALEMENT", img: IMG_DESAL, desc: "El agua de mar o salobre se vuelve potable in situ, alimentada por energía verde.", specs: "20 m³/día · 100 % renovable · 24/7" },
    ],
    prosumerTitle: "El PROSUMER — la tercera revolución industrial",
    prosumerText: "Cada sitio INGENIA produce y consume localmente su energía, su agua y su hidrógeno, y luego comparte los excedentes con sus vecinos a través de una red distribuida e inteligente.",
    offer: "La oferta INGENIA: 100 €/mes, servicio y línea directa 24/24 incluidos — instalado en una semana.",
    ctaSite: "Visitar ingenia-services.com",
    ctaContact: "Escribir al inventor",
  },
  de: {
    kicker: "INGENIA — die Industrie-Website des Erfinders",
    subtitle: "Das erste Raketenflugzeug jenseits von Mach 10",
    countdownLabel: "Erster Hyperschallflug",
    units: ["Tage", "Std.", "Min", "Sek"],
    intro: [
      "Mach 10 — rund 12.300 Stundenkilometer, fünfmal so schnell wie die Concorde. MELOMANIA ist das Raketenflugzeug-Programm von Gilles de Buretel de Chassey: diese Schwelle 2030 zu überschreiten — mit einem Demonstrator vor der bemannten Version.",
      "Wie immer bei diesem fest im Leben verwurzelten Industriellen ist Leistung nie Selbstzweck: Sie ist ein Versprechen an den Menschen — anders zu reisen, schneller, sauberer.",
    ],
    specsTitle: "Programmsteckbrief",
    specs: [
      { v: "Mach 10", l: "Zielgeschwindigkeit" },
      { v: "≈ 12.300 km/h", l: "5× die Concorde" },
      { v: "2030", l: "Erstflug" },
      { v: "Demonstrator", l: "Vor der bemannten Version" },
    ],
    logbook: {
      title: "Logbuch — der Weg nach 2030",
      steps: [
        { year: "2026", title: "Studien & Entwurf", text: "Machbarkeitsstudien, Architektur des Raketenflugzeugs, Entwurf des Demonstrators.", current: true },
        { year: "2027", title: "Antrieb", text: "Bodentests des Raketentriebwerks und der Antriebskette.", current: false },
        { year: "2028", title: "Demonstrator", text: "Erstflug des unbemannten Demonstrators, schrittweise Steigerung.", current: false },
        { year: "2029", title: "Testflüge", text: "Fortschreitende Überschall- und dann Hyperschall-Flugkampagne.", current: false },
        { year: "2030", title: "Mach 10", text: "Der Meilenstein: erster Flug mit Mach 10, fünfmal Concorde.", current: false },
      ],
    },
    familyTitle: "Die Luftfahrzeug-Familie",
    family: [
      { name: "MELOMANIA", desc: "Das erste Raketenflugzeug jenseits von Mach 10 — 2030.", featured: true },
      { name: "DER SWAN", desc: "Der Flug in seiner reinsten Eleganz.", featured: false },
      { name: "DIE MELUSINE", desc: "Die Fee der Lüfte, zwischen Traum und Ingenieurskunst.", featured: false },
    ],
    modulesTitle: "Die INGENIA-Module — Erfindung im Dienst des Alltags",
    modules: [
      { name: "ARCHANGE", img: IMG_SAT, desc: "Konnektivität über Satelliten in niedriger Umlaufbahn, im Angebot enthalten.", specs: "Niedrige Umlaufbahn · Überwachung 24/7" },
      { name: "STARBOX", img: IMG_STARBOX, desc: "Grüner Wasserstoff auf Abruf vor Ort, aus recyceltem Schrott.", specs: "120 kg H₂/Tag · 3 t Schrott verwertet/Tag · 9 t CO₂ vermieden/Monat" },
      { name: "DESSALEMENT", img: IMG_DESAL, desc: "Meer- oder Brackwasser wird vor Ort trinkbar, gespeist mit grüner Energie.", specs: "20 m³/Tag · 100 % erneuerbar · 24/7" },
    ],
    prosumerTitle: "Der PROSUMER — die dritte industrielle Revolution",
    prosumerText: "Jeder INGENIA-Standort produziert und verbraucht seine Energie, sein Wasser und seinen Wasserstoff lokal — und teilt Überschüsse über ein intelligentes, verteiltes Netz mit den Nachbarn.",
    offer: "Das INGENIA-Angebot: 100 €/Monat, Service und Hotline rund um die Uhr inklusive — installiert binnen einer Woche.",
    ctaSite: "ingenia-services.com besuchen",
    ctaContact: "Dem Erfinder schreiben",
  },
};

const useCountdown = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, TARGET - now);
  return [
    Math.floor(diff / 86400000),
    Math.floor(diff / 3600000) % 24,
    Math.floor(diff / 60000) % 60,
    Math.floor(diff / 1000) % 60,
  ];
};

const IngeniaPage = () => {
  const { lang } = useLang();
  const S = STR[lang] || STR.fr;
  const [d, h, m, s] = useCountdown();
  usePageMeta(
    "INGENIA — MELOMANIA, l'avion-fusée Mach 10 | Gilles de Chassey",
    "Le site industriel de l'inventeur : MELOMANIA, premier avion-fusée visant Mach 10 à l'horizon 2030, LE SWAN, LA MELUSINE — compte à rebours, journal de bord et hommages.",
    "/ingenia"
  );

  return (
    <div className="min-h-screen bg-inkdeep" data-testid="ingenia-page">
      <div className="relative px-5 md:px-10 pt-28 md:pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG_ROCKET} alt="" className="w-full h-full object-cover opacity-40 img-lumiere" />
          <div className="absolute inset-0 bg-gradient-to-b from-inkdeep/70 via-inkdeep/40 to-inkdeep" />
        </div>
        <div className="relative max-w-[1200px] mx-auto">
          <Reveal>
            <Link to="/" data-testid="ingenia-back-link" className="inline-flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-paper/50 hover:text-gold transition-colors mb-14">
              <ArrowLeft size={14} /> Gilles de Buretel de Chassey
            </Link>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="font-mono2 text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-terraBright mb-5">{S.kicker}</p>
            <h1 data-testid="ingenia-title" className="font-display font-light text-6xl sm:text-7xl lg:text-8xl text-paper leading-[1.0] tracking-tight">
              MELOMANIA
            </h1>
            <p data-testid="ingenia-subtitle" className="font-display italic text-2xl md:text-3xl text-gold mt-4">{S.subtitle}</p>
          </Reveal>

          <Reveal delay={0.12} className="mt-14">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-paper/50 mb-5">{S.countdownLabel}</p>
            <div className="grid grid-cols-4 max-w-2xl border border-gold/40 divide-x divide-gold/40" data-testid="melomania-countdown">
              {[d, h, m, s].map((v, i) => (
                <div key={i} className="py-6 md:py-8 text-center bg-inkdeep/70 backdrop-blur-sm">
                  <p data-testid={`countdown-${i}`} className="font-display text-3xl md:text-5xl text-gold tabular-nums leading-none">
                    {String(v).padStart(2, "0")}
                  </p>
                  <p className="font-mono2 text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-paper/50 mt-2">{S.units[i]}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.18} className="mt-14">
            <div className="space-y-6 max-w-3xl">
              {S.intro.map((p, i) => (
                <p key={i} data-testid={`ingenia-intro-${i}`} className="font-display text-xl md:text-2xl leading-relaxed text-paper/80 first:text-paper">
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      <div className="px-5 md:px-10 py-16">
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
            <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-terraBright mb-6">{S.specsTitle}</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 border border-linedark divide-x divide-y lg:divide-y-0 divide-linedark" data-testid="melomania-specs">
              {S.specs.map((sp, i) => (
                <div key={i} className="p-6 md:p-8">
                  <p className="font-display text-2xl md:text-4xl text-paper">{sp.v}</p>
                  <p className="text-xs text-paper/50 mt-2">{sp.l}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-16">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-terraBright mb-8">{S.logbook.title}</p>
            <div className="grid md:grid-cols-5 border border-linedark divide-y md:divide-y-0 md:divide-x divide-linedark" data-testid="melomania-logbook">
              {S.logbook.steps.map((st, i) => (
                <div key={i} data-testid={`logbook-step-${i}`} className={`p-6 md:p-7 ${st.current ? "bg-gold/[0.07]" : ""} hover:bg-white/[0.03] transition-colors`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className={`font-display text-3xl md:text-4xl ${st.current ? "text-gold" : "text-paper/85"}`}>{st.year}</span>
                    {st.current && <span className="w-2 h-2 bg-terraBright animate-pulse-soft shrink-0" />}
                  </div>
                  <h3 className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-terraBright mt-3">{st.title}</h3>
                  <p className="text-xs text-paper/55 leading-relaxed mt-2">{st.text}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-16">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-terraBright mb-6">{S.familyTitle}</p>
            <div className="grid md:grid-cols-3 gap-5" data-testid="ingenia-family">
              {S.family.map((f, i) => (
                <div key={i} data-testid={`ingenia-aircraft-${i}`} className={`p-8 border ${f.featured ? "border-gold/70 bg-gold/[0.06]" : "border-linedark"} hover:border-bronze transition-colors`}>
                  <h3 className="font-display text-2xl md:text-3xl text-paper">{f.name}</h3>
                  <p className="text-sm text-paper/55 leading-relaxed mt-3">{f.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      <div className="px-5 md:px-10 py-16 bg-[#14100D]">
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
            <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-terraBright mb-10">{S.modulesTitle}</p>
            <div className="grid md:grid-cols-3 gap-5" data-testid="ingenia-modules">
              {S.modules.map((mod, i) => (
                <div key={i} data-testid={`ingenia-module-${i}`} className="group border border-linedark bg-inkdeep overflow-hidden hover:border-bronze transition-colors">
                  <div className="overflow-hidden aspect-[16/10]">
                    <img src={mod.img} alt={mod.name} loading="lazy" className="w-full h-full object-cover img-lumiere group-hover:scale-[1.05] transition-transform duration-[1200ms] ease-out" />
                  </div>
                  <div className="p-7">
                    <h3 className="font-display text-2xl text-paper">{mod.name}</h3>
                    <p className="text-sm text-paper/55 leading-relaxed mt-3">{mod.desc}</p>
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-gold mt-4">{mod.specs}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-16">
            <div className="border-l-2 border-gold/60 pl-6 md:pl-10 max-w-3xl">
              <h2 data-testid="ingenia-prosumer-title" className="font-display text-3xl md:text-4xl text-paper leading-tight">{S.prosumerTitle}</h2>
              <p className="text-paper/65 leading-relaxed mt-4">{S.prosumerText}</p>
              <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-gold mt-6">{S.offer}</p>
            </div>
          </Reveal>

          <Reveal className="mt-12">
            <div className="flex flex-wrap gap-4">
              <a data-testid="ingenia-site-link" href="https://www.ingenia-services.com" target="_blank" rel="noreferrer" className="group inline-flex items-center gap-3 bg-terra text-paper hover:bg-terraBright transition-colors px-8 py-4 font-mono2 text-[11px] uppercase tracking-[0.2em]">
                {S.ctaSite}
                <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </a>
              <Link data-testid="ingenia-contact-link" to="/#contact" className="inline-flex items-center gap-3 border border-paper/40 text-paper hover:border-gold hover:text-gold transition-colors px-8 py-4 font-mono2 text-[11px] uppercase tracking-[0.2em]">
                <PenLine size={14} /> {S.ctaContact}
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default IngeniaPage;
